import { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import InputField, { INPUT_TYPE_TEXTAREA } from 'components/InputField';
import Button from 'components/Button';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@mui/material';
import Select from 'components/Select';
import cloneDeep from 'lodash/cloneDeep';
import phoneOptionsTemplate from 'components/Select/phone_options.json';

const EditConfigurationForm = ({ configuration, onSave,
  editButtonColor,
  editFontColor,
  saveButtonColor,
  saveButtonFontColor,
  backButtonColor,
  backButtonFontColor,
  ...props
}) => {
  const [lm, setLm] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const [phoneOptions, setPhoneOptions] = useState(cloneDeep(phoneOptionsTemplate));

  useEffect(() => {
    getLocaleMap([
      'errors_required',
      'save_button',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (values) => {
    const { value } = values;
    const errors = {};
    if (!value) {
      errors.value = lm.errors_required;
    }
    // if (is_user && !password) {
    //   errors.password = lm.errors_required;
    // }
    return errors;
  };

  const initialValues = useMemo(() => {
    const type = configuration?.type || '1';
    let value = configuration?.fix_value || '';
    if (type === 'country_code_blacklist') {
      value = value.split(/\s*,\s*/);
    }
    return {
      value,
      type,
    };
  }, [configuration]);

  return (
    <Formik
      onSubmit={(values, formikBag) => {
        let newValues = values;
        if (values.type === 'country_code_blacklist') {
          let { value } = newValues;
          if (value != null) {
            value = value.join(', ');
          }
          newValues = {
            ...values,
            value,
          };
        }
        return onSave(newValues, formikBag);
      }}
      initialValues={initialValues}
      validate={getFormValidation}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, setFieldValue, errors, values, touched, isSubmitting, dirty }) => {
        let inputType;
        switch (values.type) {
          case '2':
            inputType = INPUT_TYPE_TEXTAREA;
            break;
          case 'country_code_blacklist':
            inputType = 'country_code_blacklist';
            break;
          default:
            break;
        }
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={1}>
              <Grid item xs={12} mt={2}>
                <MKBox display="flex" width="100%" justifyContent="space-between">
                  <Button
                    color={backButtonColor || 'info'}
                    disabled={isSubmitting}
                    style={{
                      border: backButtonColor === 'white' && '1px solid black',
                      width: '10vw',
                    }}
                    onClick={() => { navigate(-1); }}
                    sx={{
                      color: backButtonFontColor,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    color={saveButtonColor || 'info'}
                    disabled={isSubmitting || !dirty}
                    style={{
                      width: '10vw',
                      display: (isEdit ? 'initial' : 'none'),
                    }}
                    sx={{
                      color: saveButtonFontColor,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                    ) : lm.save_button}
                  </Button>
                  {!isEdit && (
                    <Button
                      color={editButtonColor || 'info'}
                      style={{
                        width: '10vw',
                      }}
                      sx={{
                        color: editFontColor,
                      }}
                      onClick={() => setIsEdit(true)}
                    >
                      Edit
                    </Button>
                  )}
                </MKBox>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="flex-start">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    {`${configuration?.name} *`}
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  {isEdit ? (() => {
                    return inputType === 'country_code_blacklist'
                      ? (
                        <Select
                          name="value"
                          multiple
                          options={phoneOptions}
                          value={values.value}
                          onChange={(v) => {
                            setFieldValue('value', v);
                          }}
                          handleBlur={handleBlur}
                          error={touched.value && !!errors.value}
                        />
                      ) : (
                        <InputField
                          name="value"
                          label=""
                          type={inputType}
                          value={values.value}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          error={touched.value && !!errors.value}
                        />
                      )
                  })() : (
                    <MKTypography color="black">
                      {configuration?.fix_value}
                    </MKTypography>
                  )}
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.value && !!errors.value) ? errors.value : '\xa0'}
              </MKTypography>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditConfigurationForm.propTypes = {
  configuration: PropTypes.object,
  onSave: PropTypes.func,
  editButtonColor: PropTypes.string,
  editFontColor: PropTypes.string,
  saveButtonColor: PropTypes.string,
  saveButtonFontColor: PropTypes.string,
  backButtonColor: PropTypes.string,
  backButtonFontColor: PropTypes.string,
};

EditConfigurationForm.defaultProps = {
  configuration: {},
  onSave: () => {},
};

export default EditConfigurationForm;
